// STN-Runtime 诊断服务客户端。
//
// 诊断是异步作业：POST /api/diagnose 立即返回 job_id，再轮询
// GET /api/diagnose/{job_id} 直到 done / error。后端地址通过环境变量
// VUE_APP_DIAGNOSE_API 配置，默认同源 /api；开发代理到 http://127.0.0.1:8000。

import { apiJSON, BASE_URL } from './http'
import { sessionEpoch } from './session'

// 提交诊断：校验+转换+导入+入队，返回 { job_id, scene_id, entity_counts }。
async function requestApi(path, options) {
  try {
    return await apiJSON(path, options)
  } catch (error) {
    const message = '无法连接 STN-Runtime，请先启动后端服务或检查 VUE_APP_DIAGNOSE_API 配置。'
    throw new Error(error.message || message)
  }
}

export async function submitDiagnosis(contract) {
  return requestApi('/api/diagnose', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(contract)
  })
}

// 查询单次作业状态。
export async function getDiagnosis(jobId) {
  return requestApi(`/api/diagnose/${jobId}`)
}

// 提交演化：逐节点问答，返回 { job_id, scene_id, entity_counts }。
export async function submitEvolution(body) {
  return requestApi('/api/evolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 提交并轮询直到诊断完成。
 *
 * @param {Object} contract graphToContract() 的返回值
 * @param {Object} [opts]
 * @param {(status: string, job: Object) => void} [opts.onStatus] 状态回调
 * @param {number} [opts.intervalMs] 轮询间隔，默认 2000ms
 * @param {number} [opts.timeoutMs] 超时，默认 300000ms（5 分钟）
 * @returns {Promise<Object>} 完成作业的 result（含 answer/status/metrics）
 */
export async function runDiagnosis(contract, opts = {}) {
  const epoch = sessionEpoch()
  const submitted = await submitDiagnosis(contract)
  if (epoch !== sessionEpoch()) throw new Error('账号已切换，本次推演已取消。')
  return pollJob(submitted, { timeoutLabel: '诊断', ...opts, epoch })
}

/**
 * 提交演化并轮询直到完成（作业接口与诊断共用 GET /api/diagnose/{job_id}）。
 *
 * @param {Object} body /api/evolve 请求体（含 question / graph）
 * @param {Object} [opts] 同 runDiagnosis 的 onStatus / intervalMs / timeoutMs
 * @returns {Promise<Object>} 含 node_states 的作业 result
 */
export async function runEvolution(body, opts = {}) {
  const epoch = sessionEpoch()
  const submitted = await submitEvolution(body)
  if (epoch !== sessionEpoch()) throw new Error('账号已切换，本次推演已取消。')
  // 逐节点问答耗时较长，默认给更宽松的超时。
  return pollJob(submitted, { timeoutLabel: '演化', timeoutMs: 600000, ...opts, epoch })
}

export async function runSceneEvent(scene,event,graph,opts = {}) {
  const epoch = sessionEpoch()
  const submitted = await requestApi(`/api/saved-scenes/${encodeURIComponent(scene.id)}/events/${encodeURIComponent(event.id)}/run`, {
    method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({expected_version:scene.version,graph})
  })
  if (epoch !== sessionEpoch()) throw new Error('账号已切换，本次推演已取消。')
  return pollJob(submitted,{timeoutLabel:'场景演化',timeoutMs:600000,...opts,epoch})
}

// 轮询一个已提交作业直到 done / error / 超时。
async function pollJob(submitted, opts = {}) {
  const epoch = opts.epoch ?? sessionEpoch()
  const intervalMs = opts.intervalMs || 2000
  const timeoutMs = opts.timeoutMs || 300000
  const onStatus = opts.onStatus || (() => {})
  const label = opts.timeoutLabel || '任务'

  onStatus('pending', submitted)
  const deadline = Date.now() + timeoutMs
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (Date.now() > deadline) {
      throw new Error(`${label}超时，请稍后重试或查看后端日志。`)
    }
    await delay(intervalMs)
    if (epoch !== sessionEpoch()) throw new Error('账号已切换，本次推演已取消。')
    const job = await getDiagnosis(submitted.job_id)
    onStatus(job.status, job)
    if (job.status === 'done') return job.result
    if (job.status === 'error') {
      throw new Error(job.error || `${label}失败。`)
    }
  }
}

export { BASE_URL }

